"use client";

import { useEffect, useRef, useState } from "react";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  HINT,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import JeopardyBoard from "@/components/tools/games-organizers/jeopardy-board";
import JeopardyClue from "@/components/tools/games-organizers/jeopardy-clue";
import JeopardyScores from "@/components/tools/games-organizers/jeopardy-scores";
import JeopardySetup, {
  type JeopardySettings,
} from "@/components/tools/games-organizers/jeopardy-setup";
import {
  FULL_SCREEN_FRAME,
  useFullScreen,
} from "@/components/tools/games-organizers/use-full-screen";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  awardPoints,
  buildTeams,
  cellKey,
  clueCount,
  DEFAULT_STEP,
  leaders,
  parseBoard,
  parseTeamNames,
} from "@/lib/tool-kit/games-organizers/jeopardy";

/** English strings. A translated content file overrides these through `ui`. */
export const JEOPARDY_GAME_MAKER_STRINGS = {
  boardLabel:
    "Your board — a line starting with # opens a category, then one clue per line as clue | response",
  boardHint:
    "Up to {maxCategories} categories with up to {maxRows} clues each. To set a clue's own points, start the line with a number, as in 300 | clue | response.",
  boardPlaceholder:
    "# Category name\nFirst clue | Its response\nSecond clue | Its response\n\n# Another category\nFirst clue | Its response",
  example:
    '# Citation basics\nThis list at the end of an APA paper is titled this | What is References?\nThis Latin abbreviation, meaning "and others", shortens a long list of authors | What is et al.?\nThis string of characters gives a journal article a permanent online address | What is a DOI?\nPresenting someone else\'s words or ideas as your own | What is plagiarism?\n\n# Citation styles\nThe humanities style whose list of sources is called Works Cited | What is MLA?\nThe style used by many psychology and social-science journals | What is APA?\nThis style offers both a notes-and-bibliography system and an author-date system | What is Chicago?\nThe numbered style widely used in medicine | What is Vancouver?\n\n# Research words\nA diary written by a witness is this kind of source | What is a primary source?\nThe short summary at the start of a journal article | What is an abstract?\nExperts check an article before a journal publishes it | What is peer review?\nA list of sources with a short note on each one | What is an annotated bibliography?\n\n# Writing\nThe one-sentence claim an essay argues | What is a thesis statement?\nPutting a passage in your own words, still with a citation | What is paraphrasing?\nWords copied exactly and put in quotation marks | What is a direct quotation?\nThe last check of spelling, grammar and punctuation | What is proofreading?',
  loadExample: "Load example",
  stepLabel: "Points added per row",
  stepHint: "The first row is worth one step, the second two steps, and so on.",
  teamsLabel: "Teams",
  teamsOption: "one:{count} team|other:{count} teams",
  namesLabel: "Team names (optional, separated by commas)",
  namesPlaceholder: "Owls, Foxes, Herons",
  teamName: "Team {n}",
  remember: "Remember this board on this device",
  rememberNote:
    "Saved only in this browser, on this device. Nothing is uploaded, and unticking the box deletes the saved board.",
  clearSaved: "Clear board",
  start: "Start the game",
  categoriesCount: "one:{count} category|other:{count} categories",
  cluesCount: "one:{count} clue|other:{count} clues",
  summary: "Ready: {categories}, {clues}.",
  problemNoCategory: "Line {line}: a clue needs a # category line above it.",
  problemEmptyClue: "Line {line}: this clue is empty and is left out.",
  problemEmptyCategory:
    "Line {line}: this category has no clues and is left out.",
  problemExtraCategory:
    "Line {line}: the board holds {max} categories, so this one is left out.",
  problemExtraClue:
    "Line {line}: a category holds {max} clues, so this one is left out.",
  needBoard: "Add at least one category with one clue to play.",
  heading: "Review board",
  progress: "{played} of {total} clues played",
  fullScreen: "Full screen",
  exitFullScreen: "Exit full screen",
  endGame: "End game",
  boardAria: "Clue board",
  cellLabel: "{category}, {points} points",
  cellDone: "{category}, {points} points, already played",
  clueHeading: "{category} for {points}",
  showResponse: "Show response",
  responseLabel: "Response:",
  noResponse: "This clue has no response.",
  awardHeading: "Award points",
  addAria: "Add {points} points to {team}",
  takeAria: "Take {points} points from {team}",
  backToBoard: "Back to the board",
  clueKeys:
    "Space or Enter shows the response. Press 1–6 to add the points to that team, or hold Shift and press the number to take them away. B returns to the board.",
  scoresLabel: "Team scores",
  points: "one:point|other:points",
  doneHeading: "Board cleared",
  leading: "Leading: {teams}",
  noLeader: "Nobody has scored yet.",
  playAgain: "Play the same board again",
  backToEditor: "Back to the board editor",
} as const;

type Phase = "setup" | "play" | "done";

const STORAGE_KEY = "citationeasy.jeopardy.v1";

interface Saved {
  text: string;
  step: number;
  teams: number;
  names: string;
}

function readSaved(): Saved | null {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const data = JSON.parse(raw) as Partial<Saved>;
    return typeof data.text === "string"
      ? {
          text: data.text,
          step: Number(data.step) || DEFAULT_STEP,
          teams: Number(data.teams) || 3,
          names: typeof data.names === "string" ? data.names : "",
        }
      : null;
  } catch {
    return null;
  }
}

function writeSaved(value: Saved | null): void {
  try {
    if (value) {
      globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(value));
    } else {
      globalThis.localStorage?.removeItem(STORAGE_KEY);
    }
  } catch {
    // Storage can be blocked (private windows, site settings); the game still works.
  }
}

const INITIAL: JeopardySettings = {
  text: "",
  step: DEFAULT_STEP,
  teams: 3,
  names: "",
  remember: false,
};

interface OpenClue {
  column: number;
  row: number;
  revealed: boolean;
  /** True once the response was shown or points were awarded: closing then plays the clue. */
  touched: boolean;
}

export default function JeopardyGameMakerWidget() {
  const strings = useToolUi(JEOPARDY_GAME_MAKER_STRINGS);
  const fmt = useToolNumber();
  const frame = useRef<HTMLDivElement>(null);
  const screen = useFullScreen(frame);
  const [settings, setSettings] = useState<JeopardySettings>(INITIAL);
  const [phase, setPhase] = useState<Phase>("setup");
  const [used, setUsed] = useState<ReadonlySet<string>>(new Set());
  const [scores, setScores] = useState<number[]>([]);
  const [open, setOpen] = useState<OpenClue | null>(null);
  const [error, setError] = useState(false);

  const { text, step, teams: teamCount, names, remember } = settings;
  const board = parseBoard(text, step);
  const teamNames = buildTeams(teamCount, parseTeamNames(names), (n) =>
    t(strings.teamName, { n: fmt(n) })
  );
  const total = clueCount(board.categories);

  useEffect(() => {
    const saved = readSaved();
    if (saved) {
      setSettings((current) => ({ ...current, ...saved, remember: true }));
    }
  }, []);

  useEffect(() => {
    if (remember && text.trim() !== "") {
      writeSaved({ text, step, teams: teamCount, names });
    }
  }, [remember, text, step, teamCount, names]);

  const wasOpen = useRef(false);
  useEffect(() => {
    if (wasOpen.current && !open) {
      frame.current
        ?.querySelector<HTMLButtonElement>("[data-cell]:not(:disabled)")
        ?.focus();
    }
    wasOpen.current = open !== null;
  }, [open]);

  useEffect(() => {
    function onEscape(event: KeyboardEvent) {
      if (event.key === "Escape" && !open) {
        screen.set(false);
      }
    }
    window.addEventListener("keydown", onEscape);
    return () => window.removeEventListener("keydown", onEscape);
  });

  function update(patch: Partial<JeopardySettings>) {
    setSettings((current) => ({ ...current, ...patch }));
  }

  function begin() {
    if (total === 0) {
      setError(true);
      return;
    }
    setError(false);
    setUsed(new Set());
    setScores(teamNames.map(() => 0));
    setOpen(null);
    setPhase("play");
  }

  function award(team: number, points: number) {
    setScores((current) => awardPoints(current, team, points));
    setOpen((current) => (current ? { ...current, touched: true } : current));
  }

  function closeClue() {
    if (!open) {
      return;
    }
    if (open.touched) {
      const next = new Set(used).add(cellKey(open.column, open.row));
      setUsed(next);
      if (next.size >= total) {
        setPhase("done");
      }
    }
    setOpen(null);
  }

  function back() {
    if (screen.wide) {
      screen.set(false);
    }
    setOpen(null);
    setPhase("setup");
  }

  const category = open ? board.categories[open.column] : undefined;
  const clue = open ? category?.clues[open.row] : undefined;
  const top = leaders(scores);

  return (
    <div
      className={`space-y-6 ${screen.wide ? FULL_SCREEN_FRAME : ""}`}
      ref={frame}
    >
      {phase === "setup" ? (
        <JeopardySetup
          board={board}
          error={error}
          onChange={update}
          onClear={() => {
            update({ text: "" });
            writeSaved(null);
          }}
          onRemember={(on) => {
            update({ remember: on });
            if (!on) {
              writeSaved(null);
            }
          }}
          onStart={begin}
          settings={settings}
          strings={strings}
        />
      ) : null}

      {phase === "play" ? (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="space-y-1">
              <h2 className={WIDGET_H2}>{strings.heading}</h2>
              <p className={HINT}>
                {t(strings.progress, {
                  played: fmt(used.size),
                  total: fmt(total),
                })}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                aria-pressed={screen.wide}
                className={BUTTON_SECONDARY}
                onClick={() => screen.set(!screen.wide)}
                type="button"
              >
                {screen.wide ? strings.exitFullScreen : strings.fullScreen}
              </button>
              <button className={BUTTON_SECONDARY} onClick={back} type="button">
                {strings.endGame}
              </button>
            </div>
          </div>
          {open && category && clue ? (
            <JeopardyClue
              category={category.name}
              clue={clue}
              onAward={award}
              onClose={closeClue}
              onReveal={() =>
                setOpen({ ...open, revealed: true, touched: true })
              }
              revealed={open.revealed}
              scores={scores}
              strings={strings}
              teams={teamNames}
            />
          ) : (
            <div className="space-y-5">
              <JeopardyBoard
                categories={board.categories}
                onOpen={(column, row) =>
                  setOpen({ column, row, revealed: false, touched: false })
                }
                rows={board.rows}
                strings={strings}
                used={used}
              />
              <JeopardyScores
                scores={scores}
                strings={strings}
                teams={teamNames}
              />
            </div>
          )}
        </div>
      ) : null}

      {phase === "done" ? (
        <div className="space-y-4 text-center">
          <h2 className={WIDGET_H2}>{strings.doneHeading}</h2>
          <JeopardyScores
            highlight={top}
            scores={scores}
            strings={strings}
            teams={teamNames}
          />
          <p aria-live="polite" className="text-foreground text-lg">
            {top.length > 0
              ? t(strings.leading, {
                  teams: top.map((i) => teamNames[i]).join(", "),
                })
              : strings.noLeader}
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <button className={BUTTON_PRIMARY} onClick={begin} type="button">
              {strings.playAgain}
            </button>
            <button className={BUTTON_SECONDARY} onClick={back} type="button">
              {strings.backToEditor}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
